import { createFriendlyName } from "../lib/conversionUtils";
import { LLMMetadata } from "../lib/llm/types";
import { parseCharacterRole } from "../lib/parserUtils";
import { PaymentTier } from "./payment";
import { IssuePriority } from "./roadmapAnalysis";

/**
 * Represents the impact score for a chapter or other book elements.
 * Used to track the quality and completeness of different aspects
 * of the chapter's writing and revision process.
 */
export interface ImpactScore {
    score: number;
    explanation: string;
    priorityColor: string;
    priorityColorBackground: string;
    priorityColorBorder: string;
    scoreString: string;
}

/**
 * Represents the status of various progress metrics for a chapter or other book elements.
 * Used to track the completion state of different aspects of writing and editing.
 */
export enum ProgressStatus {
    /** Initial state, indicating no significant work has been done */
    Incomplete = "incomplete",
    /** Work has begun but is not yet finished */
    InProgress = "in-progress",
    /** Work has been finished and reviewed */
    Complete = "complete",
}

export type CharacterType =
    | "protagonist"
    | "antagonist"
    | "supporting"
    | "minor"
    | "unknown";

export type WorkAnalysisCategory =
    | "identification"
    | "analysis"
    | "summary"
    | "generation";

interface RemoteFileMetadata {
    uri?: string;
    name?: string;
    state?: number | string;
    mime_type?: string;
    size_bytes?: number | string;
    create_time?: string;
    sha256_hash?: string;
    update_time?: string;
    display_name?: string;
    expiration_time?: string;
}

/**
 * Represents a character in the book with their complete profile and narrative purpose.
 * Characters are linked together through their relationships array, forming a character web.
 */
export interface Character {
    /** Unique identifier used for character references and relationships */
    id: string;
    /** Character's unique friendly id */
    friendly_id?: string;
    /** Character's full name as it appears in the story */
    name: string;
    /** The character's primary function in the narrative */
    role: CharacterType;
    /** Physical and personality traits of the character */
    description: string;
    /** Character's function in the story */
    function: string;
    /** The character's situation in the story */
    situation: string;
    /** Aliases or nicknames of the character */
    aliases: string[];
    /** The character's significance in the story */
    significance_score: number;

    summary?: string;
    arcs?: string;
    relationships?: string;
    consistency?: string;
    impact?: string;
    areas_for_improvement?: string[];

    significance?: {
        plot_impact: number;
        screen_time: number;
        character_development: number;
        relationship_complexity: number;
        thematic_relevance: number;
    };
}

/**
 * Represents the progress metrics for a chapter's development.
 * Used to track the quality and completeness of different aspects
 * of the chapter's writing and revision process.
 */
export interface ChapterProgress {
    /**
     * Measures the readability and clarity of the text
     * Considers factors like sentence structure, word choice, and flow
     */
    readability: ProgressStatus;
    /**
     * Tracks the development and depth of characters in the chapter
     * Evaluates character actions, dialogue, and growth
     */
    characterDevelopment: ProgressStatus;
    /**
     * Overall assessment of the chapter's readiness for publication
     * Considers plot progression, pacing, and narrative cohesion
     */
    storyReadiness: ProgressStatus;
}

export type PlotPointType =
    | "hook"
    | "inciting_incident"
    | "plot_turn_1"
    | "pinch_point_1"
    | "midpoint"
    | "pinch_point_2"
    | "plot_turn_2"
    | "crisis"
    | "climax"
    | "resolution"
    | "rising_action";

export type NarrativeMetric =
    | "pacing_score"
    | "tension_score"
    | "emotional_intensity_score"
    | "character_development_score"
    | "relationship_dynamics_score"
    | "reading_score"
    | "scene_rhythm_score"
    | "tension_curve_score"
    | "temporal_urgency_score"
    | "threat_escalation_score"
    | "narrative_momentum_score"
    | "antagonist_presence_score"
    | "investigation_momentum_score"
    | "plot_twist_effectiveness_score"
    | "character_development_pace_score"
    | string; // Allow any string to support dynamic metrics

export type PlotPoint = {
    type: PlotPointType;
    description: string;
    impact_level: number;
};

/**
 * Represents a single chapter in a book, containing both content
 * and metadata about the chapter's development status.
 */
export interface Chapter {
    /**
     * Unique identifier for referencing the chapter
     **/
    id: string;

    /**
     * Unique identifier for referencing the chapter
     **/
    friendly_id: string;

    /**
     * Display title of the chapter
     */
    title: string;

    /**
     * Number of the chapter in the book
     */
    chapter_number?: number;

    /**
     * Summary or overview of the chapter's main plot
     * Useful for providing a high-level overview of the chapter's content
     * and its relationship to the overall book's plot
     */
    summary?: string;

    /**
     * Main content/text of the chapter
     * Should be properly formatted with paragraphs and dialogue
     */
    content?: string;

    /**
     * Progress metrics tracking the chapter's development
     * Used for editorial workflow and quality assessment
     */
    progress?: ChapterProgress;

    /**
     * Main characters or protagonists in the chapter
     * Describes the main characters or protagonists in the chapter
     */
    main_characters?: string;

    /**
     * Significant events or milestones in the chapter
     * Highlight any major events or significant moments in the chapter
     */
    major_events?: string;

    /**
     * Writing style or tone of the chapter
     * Reflects the overall writing style or tone of the chapter
     */
    writing_style?: string;

    /**
     * Pacing or timing of the chapter
     * Describes the pacing or timing of the chapter
     */
    pacing?: string;

    /**
     * Themes or themes of the chapter
     * Describes the themes or themes of the chapter
     */
    themes?: string;

    /**
     * Story arc or plot progression of the chapter
     * Describes the story arc or plot progression of the chapter
     */
    story_arc?: string;

    /**
     * Recommendations or suggestions for the chapter
     * Provides guidance or suggestions for the chapter's development
     */
    recommendations?: string[];

    /**
     * Narrative metrics for the chapter
     * Describes the narrative metrics or metrics of the chapter.
     */
    narrative_metrics?: Record<NarrativeMetric, number>;

    /**
     * Plot points for the chapter
     * Describes the plot points or significant moments in the chapter.
     */
    plot_points?: PlotPoint[];

    /** The Flesch-Kincaid readability score for the chapter */
    reading_score?: number;

    original_metrics?: any; // Add this field to store the original metrics data
}

export interface BookStats {
    wordCount: number;
}

// Types based on the schema
export type Work = {
    id: string;
    friendly_id: string;
    user_id: string;
    title: string;
    author: string;
    work_type: string;
    is_public: boolean;
    metadata: any;
    created_at: Date;
    updated_at: Date;
    payment_tier: PaymentTier;
    remote_file_metadata: RemoteFileMetadata;
    tags: string;
};

export type WorkAnalysisState =
    | "notstarted"
    | "queued"
    | "processing"
    | "completed"
    | "error";

export type AnlaysisMetric = {
    impactScore: ImpactScore;
    value: IssuePriority;
    reason: string;
    label: string;
};

export const IMPACT_SCORE_LABELS = {
    HIGH: {
        scoreString: "Well-Crafted",
        priorityColor: "text-white",
        newPriorityColor: "text-impact-high",
        prioritOnlyTextColor: "text-impact-high",
        priorityColorBackground: "bg-impact-high",
        newPriorityColorBackground: "bg-impact-high/50",
        chapterBoxes:
            "bg-impact-high/75 hover:bg-impact-high/80 dark:bg-impact-high/75",
        priorityColorBorder: "border-impact-high",
        explanation: "Exceptionally well-crafted.",
        longExplanation:
            "This element aligns closely with widely observed patterns, conventions, or expectations across similar works. It demonstrates a high level of craft in a way that suggests strong alignment with genre norms or narrative intent, and likely requires little or no revision.",
    },
    MEDIUM: {
        scoreString: "Consider revision",
        priorityColor: "text-white",
        newPriorityColor: "text-impact-medium",
        prioritOnlyTextColor: "text-impact-medium",
        priorityColorBackground: "bg-impact-medium",
        newPriorityColorBackground: "bg-impact-medium/50",
        chapterBoxes:
            "bg-impact-medium/75 hover:bg-impact-medium/80 dark:bg-impact-medium/75",
        priorityColorBorder: "border-impact-medium",
        explanation: "Shows strong potential with some areas for improvement.",
        longExplanation:
            "This element performs moderately well against expected patterns, but there may be inconsistencies, missed opportunities, or areas that could benefit from refinement. It’s worth reviewing to ensure it supports your story’s goals.",
    },
    LOW: {
        scoreString: "Revision recommended",
        priorityColor: "text-white",
        newPriorityColor: "text-impact-low",
        prioritOnlyTextColor: "text-impact-low",
        priorityColorBackground: "bg-impact-low",
        priorityColorBorder: "border-impact-low",
        newPriorityColorBackground: "bg-impact-low/50",
        chapterBoxes:
            "bg-impact-low/75 hover:bg-impact-low/80 dark:bg-impact-low/75",
        explanation: "Needs significant improvement.",
        longExplanation:
            "This element diverges from genre norms or best practices in storytelling structure. It may be unclear, inconsistent, or lacking key components, and might benefit from focused revision and deeper consideration. Important note: Deviation doesn’t necessarily mean it’s wrong—it may simply reflect a creative choice that goes against common editorial wisdom.",
    },
    NA: {
        scoreString: "Not Applicable",
        priorityColor: "text-white",
        newPriorityColor: "text-impact-na",
        priorityColorBackground: "bg-impact-na",
        newPriorityColorBackground: "bg-impact-na/50",
        priorityColorBorder: "border-impact-na",
        chapterBoxes: "",
        explanation: "This metric is not applicable to this element.",
        longExplanation:
            "This score does not apply to the current element due to its nature, context, or genre-specific irrelevance. Not all metrics are relevant for every manuscript or section, and that’s completely normal.",
    },
};

/**
 * Finds the sub genre by its sub-genre ID
 * @param subGenreId - The ID of the sub-genre
 * @returns The genre object if found, otherwise undefined
 */
export function findSubGenreById(subGenreId: string): SubGenre | undefined {
    return Object.values(GENRE_REGISTRY).find((genre) =>
        genre.sub_genres.some((sub) => sub.id === subGenreId)
    );
}

export const GENRE_REGISTRY: Record<string, Genre> = {
    literary: {
        id: "literary",
        name: "Literary Fiction",
        description:
            "Fiction that prioritizes style, character development, and thematic depth over plot mechanics. Often explores complex social, philosophical, or personal issues through sophisticated prose and narrative techniques.",
        sub_genres: [
            {
                id: "literary.contemporary-literary",
                name: "Contemporary Literary",
                description:
                    "Literary works set in the present day, often examining current social issues, personal identity, and the complexities of the modern human condition.",
            },
            {
                id: "literary.historical-literary",
                name: "Historical Literary",
                description:
                    "Literary works set in the past, combining meticulous historical settings with deep character exploration and thematic richness.",
            },
            // Removed: literary.experimental
        ],
    },
    // Removed: contemporary
    romance: {
        id: "romance",
        name: "Romance",
        description:
            "Stories centered on the development and progression of a romantic relationship, typically concluding with an emotionally satisfying and optimistic ending.",
        sub_genres: [
            {
                id: "romance.contemporary-romance",
                name: "Contemporary Romance",
                description:
                    "Love stories set in the modern world, reflecting current dating dynamics, relationship challenges, and social norms.",
            },
            {
                id: "romance.historical-romance",
                name: "Historical Romance",
                description:
                    "Romantic narratives set in past eras, often featuring period-specific courtship rituals, social conventions, and historical backdrops.",
            },
            // Removed: romance.paranormal-romance
            {
                id: "romance.romantic-comedy",
                name: "Romantic Comedy",
                description:
                    "Light-hearted romance where humor is a key element, often employing witty dialogue, amusing situations, and familiar rom-com tropes.",
            },
            // Removed: romance.erotic-romance
        ],
    },
    thriller: {
        id: "thriller",
        name: "Thriller",
        description:
            "Fast-paced narratives designed to generate suspense, excitement, and tension. Typically involve danger, high stakes, plot twists, and a sense of impending threat.",
        sub_genres: [
            {
                // Added: thriller.contemporary-thriller
                id: "thriller.contemporary-thriller",
                name: "Contemporary Thriller",
                description:
                    "Thrillers set in the present day, often incorporating modern technology, current events, or contemporary societal anxieties into the plot.",
            },
            {
                id: "thriller.crime-thriller",
                name: "Crime Thriller",
                description:
                    "High-stakes stories focused on criminal activities, emphasizing suspense and danger. Often told from multiple perspectives (criminal, victim, law enforcement).",
            },
            {
                id: "thriller.psychological-thriller",
                name: "Psychological Thriller",
                description:
                    "Thrillers focusing on the characters' unstable mental states, perception, and manipulation. Often features unreliable narrators and blurred reality.",
            },
            // Removed: thriller.political-thriller
            // Removed: thriller.legal-thriller
            // Removed: thriller.technothriller
        ],
    },
    mystery: {
        id: "mystery",
        name: "Mystery",
        description:
            "Stories centered on solving a crime or uncovering a secret. Typically follow a protagonist (detective, amateur sleuth) gathering clues and unraveling the puzzle.",
        sub_genres: [
            {
                id: "mystery.crime-fiction",
                name: "Crime Fiction",
                description:
                    "Narratives focused on crime and its investigation. Often delves into police procedures, criminal psychology, or the darker aspects of criminal life.",
            },
            // Removed: mystery.police-procedural
            {
                id: "mystery.cozy-mystery",
                name: "Cozy Mystery",
                description:
                    "Lighter mysteries, typically with minimal violence or explicit content. Often set in small communities featuring amateur detectives and quirky characters.",
            },
            // Removed: mystery.amateur-sleuth
            // Removed: mystery.private-investigator
        ],
    },
    "science-fiction": {
        id: "science-fiction",
        name: "Science Fiction (Sci-Fi / SF)", // Updated Name
        description:
            "Fiction exploring the impact of imagined or extrapolated science and technology on society, individuals, or the universe. Often set in the future or alternative realities.",
        sub_genres: [
            {
                id: "science-fiction.hard-sf",
                name: "Hard SF",
                description:
                    "Science fiction prioritizing scientific accuracy and technical plausibility. Often explores specific scientific concepts or technological advancements in detail.",
            },
            {
                id: "science-fiction.science-fantasy",
                name: "Science Fantasy",
                description:
                    "A blend combining elements of science fiction (like technology, space travel) with elements of fantasy (like magic, mythical creatures).",
            },
        ],
    },
    fantasy: {
        id: "fantasy",
        name: "Fantasy",
        description:
            "Fiction characterized by magical elements, supernatural phenomena, and often set in imaginary worlds distinct from the known reality.",
        sub_genres: [
            {
                id: "fantasy.epic-high-fantasy",
                name: "Epic/High Fantasy",
                description:
                    'Large-scale fantasy often set in invented "secondary" worlds. Typically features complex magic systems, mythical beings, and world-altering conflicts.',
            },
            {
                id: "fantasy.contemporary-fantasy",
                name: "Contemporary Fantasy",
                description:
                    "Fantasy set primarily in the modern, real world, but incorporating magical or supernatural elements alongside the mundane. (Often overlaps with Urban Fantasy).",
            },
        ],
    },
    "historical-fiction": {
        id: "historical-fiction",
        name: "Historical Fiction",
        description:
            "Fiction set in a recognizable period of the past, weaving fictional characters and plotlines into historically accurate settings and events.",
        sub_genres: [
            {
                id: "historical-fiction.historical-romance",
                name: "Historical Romance",
                description:
                    "Love stories unfolding within a specific historical period, blending romantic plotlines with period-accurate details, social customs, and settings.",
            },
            {
                id: "historical-fiction.historical-literary",
                name: "Historical Literary",
                description:
                    "Literary works set in the past, combining meticulous historical settings with deep character exploration and thematic richness.",
            },
            {
                id: "historical-fiction.historical-mystery",
                name: "Historical Mystery",
                description:
                    "Mystery plots set in a past era, where the investigation unfolds against a backdrop of historical accuracy, utilizing period-specific methods and details.",
            },
        ],
    },
    memoir: {
        id: "memoir",
        name: "Memoir",
        description:
            "Non-fiction narratives based on the author's personal memories and life experiences, told using literary storytelling techniques to engage the reader.",
        sub_genres: [
            {
                id: "memoir.general-memoir",
                name: "General Memoir",
                description:
                    "Narratives detailing significant events, periods, or themes from the author's life. These memoirs blend personal reflection with literary storytelling to explore a range of experiences, without being defined primarily by a specialized focus like humor.",
            },
            {
                id: "memoir.humorous-memoir",
                name: "Humorous Memoir",
                description:
                    "Autobiographical narratives emphasizing the comedic, absurd, or light-hearted aspects of the author's life experiences.",
            },
        ],
    },
};

/**
 * Base Work class that matches the database schema
 */
export class BaseWork implements Work {
    id: string;
    friendly_id: string;
    user_id: string;
    title: string;
    author: string;
    work_type: string;
    is_public: boolean;
    metadata: any;
    created_at: Date;
    updated_at: Date;
    payment_tier: PaymentTier;
    remote_file_metadata: RemoteFileMetadata;
    tags: string;

    constructor(workData: Work) {
        if (!workData) {
            throw new Error("Book data is undefined");
        }
        if (!workData.title) {
            throw new Error("Book title is undefined");
        }

        this.id = workData.id;
        this.friendly_id = workData.friendly_id;
        this.user_id = workData.user_id;
        this.title = workData.title;
        this.author = workData.author;
        this.work_type = workData.work_type;
        this.is_public = workData.is_public;
        this.metadata = workData.metadata || {};
        this.created_at = new Date(workData.created_at);
        this.updated_at = new Date(workData.updated_at);
        this.payment_tier = workData.payment_tier;
        this.remote_file_metadata = workData.remote_file_metadata;
        this.tags = workData.tags || "";
    }

    // Method to serialize the work back to a database-compatible format
    toJSON(): Work {
        return {
            id: this.id,
            friendly_id: this.friendly_id,
            user_id: this.user_id,
            title: this.title,
            author: this.author,
            work_type: this.work_type,
            is_public: this.is_public,
            metadata: this.metadata,
            created_at: this.created_at,
            updated_at: this.updated_at,
            payment_tier: this.payment_tier,
            remote_file_metadata: this.remote_file_metadata,
            tags: this.tags,
        };
    }
}

/**
 * Represents a complete book with all its components and metadata.
 * This is the root interface that brings together all elements
 * of the book's content and structure.
 */
export class Book extends BaseWork {
    genre: string = "";
    sub_genre: string = "";
    summary: string = "";
    author: string = "";
    chapters: Chapter[] = [];
    characters: Character[] = [];
    plot_structure: string = "";
    reading_level: string = "";
    pacing: string = "";
    hooks: string = "";
    notable_structure: string = "";
    point_of_view: string = "";
    content_warnings: string = "";
    setting: string = "";
    themes: string = "";
    tone: string = "";
    writing_style: string = "";
    characterGraph: string = "";
    stats: BookStats = { wordCount: 0 };
    tags: string;

    constructor(workData: Work) {
        super(workData);

        if (!workData.metadata) {
            throw new Error("Book metadata is undefined");
        }

        this.genre = workData.metadata.genre || "";
        this.sub_genre = workData.metadata.sub_genre || "";
        this.summary = workData.metadata.summary || "";
        this.author = workData.author || "";

        // Process chapters if they exist
        if (workData.metadata?.chapters?.length > 0) {
            this.chapters = workData.metadata.chapters.map(
                (chapter: {
                    id: string;
                    friendly_id: string;
                    chapter_title?: string;
                    summary?: string;
                    content?: string;
                    progress?: ProgressStatus;
                }) => ({
                    id: chapter.id,
                    title: chapter.chapter_title || chapter.id,
                    friendly_id:
                        chapter.friendly_id ||
                        createFriendlyName(chapter.chapter_title || chapter.id),
                    summary: chapter.summary || "",
                    content: chapter.content || "",
                    progress: chapter.progress || ProgressStatus.Incomplete,
                })
            );
        }

        // Process characters if they exist
        if (workData.metadata?.characters?.length > 0) {
            this.characters = workData.metadata.characters.map(
                (character: {
                    id: string;
                    name?: string;
                    role: string;
                    description: string;
                    function: string;
                    situation: string;
                    aliases: string[];
                    significance_score: number;
                }) => ({
                    id: character.id,
                    name: character.name || character.id,
                    role: parseCharacterRole(character.role),
                    description: character.description || "",
                    function: character.function || "",
                    situation: character.situation || "",
                    aliases: Array.isArray(character.aliases)
                        ? character.aliases
                        : [],
                    significance_score:
                        typeof character.significance_score === "number"
                            ? character.significance_score
                            : 0,
                })
            );
        }

        // Initialize optional properties from metadata
        this.plot_structure = workData.metadata.plot_structure || "";
        this.reading_level = workData.metadata.reading_level || "";
        this.pacing = workData.metadata.pacing || "";
        this.hooks = workData.metadata.hooks || "";
        this.notable_structure = workData.metadata.notable_structure || "";
        this.point_of_view = workData.metadata.point_of_view || "";
        this.content_warnings = workData.metadata.content_warnings || "";
        this.setting = workData.metadata.setting || "";
        this.themes = workData.metadata.themes || "";
        this.tone = workData.metadata.tone || "";
        this.writing_style = workData.metadata.writing_style || "";
        this.characterGraph = workData.metadata.characterGraph || "";
        this.stats = workData.metadata.stats || { wordCount: 0 };
        this.tags = workData.tags || "";
    }
}

/**
 * Represents a simplified version of the Book interface containing only essential metadata.
 * Used for listing books without loading their full content.
 */
export interface BookMetaDataOnly {
    /** Unique identifier for the book */
    id: string;
    /** Main title of the book */
    title: string;
    /** Number of chapters in the book */
    description: string;
}

/**
 * Response interface for retrieving multiple books' metadata.
 * Used for API responses when listing available books.
 */
export interface GetBooksMetaDataOnly {
    /** Array of book metadata objects */
    books: BookMetaDataOnly[];
}

/** Represents a single analysis for a book */
export interface BookAnalysis {
    id: string;
    rawAnalysis: any;
    summarizedAnalysis: string;
    type: WorkAnalysisDefinition;
    scoreNumerator: number;
    scoreDenominator: number;
}

/** Represents a type of book analysis */
export interface WorkAnalysisDefinition {
    id: string;
    displayName: string;
    description: string;
    promptKey: string;
    analysisCategory: WorkAnalysisCategory;
    /**
     * Whether to show this analysis in the sidebar
     */
    showAnalysis: boolean;
    /**
     * The group this analysis belongs to (e.g. "analysis", "extras")
     */
    group?: string;
    /**
     * Boolean indicating that this analysis is a wrapper for multiple analyses.
     * A good example is the "character-analysis" analysis, which is a wrapper
     * for multiple character analyses
     */
    generatesMultipleAnalyses?: boolean;
}

/**
 * Metadata for a cloud file
 */
export interface CloudFileMetadata {
    displayName: string;
    mimeType: string;
    uri: string;
    type: "section" | "static"; // helps identify the source
    uploadTime: Date;
}

// Load the additional genre anlaysis from the genres.json file

export type Genre = {
    id: string;
    name: string;
    description: string;
    sub_genres: SubGenre[];
};

export type SubGenre = {
    id: string;
    name: string;
    description: string;
};

export type WorkAnalysis = {
    id: string;
    work_id: string;
    analysis_category: WorkAnalysisCategory;
    analysis_type: string;
    analysis_json: any;
    analysis_md: string;
    remote_file_metadata: RemoteFileMetadata | null;
    created_at: Date;
    updated_at: Date;
    state: WorkAnalysisState;
    error?: string;
    metadata?: LLMMetadata;
    cached?: boolean; // Indicates if this analysis was retrieved from cache
    score?: number;
};

export type Section = {
    id: string;
    friendly_id: string;
    work_id: string;
    title: string;
    order_index: number;
    content: string;
    vector_embedding: any;
    created_at: Date;
    updated_at: Date;
    remote_file_metadata: RemoteFileMetadata;
};
